import { stringToSlug } from '@scentregroup/shared/helpers/slug'
export { IssueType, IssueSeverity } from '@scentregroup/shared/types'
import type { IIssue as BaseIIssue } from '@scentregroup/shared/types'
import type { IMarketplaceProductPromotion } from '@scentregroup/shared/types'
import type { ICategory } from './marketplace-categories'
import type { IProductImage, IProductModifier } from './product'
import { Product } from 'schema-dts'

export type IIssue = BaseIIssue

export interface IMarketplaceProductInformationText {
  label: string
  text: string
}

export interface IMarketplaceProductInformationTable {
  label: string
  entries: IMarketplaceProductInformationText[] | undefined
}

export type IMarketplaceProductInformationEntry =
  | IMarketplaceProductInformationText
  | IMarketplaceProductInformationTable

export interface IGetMarketPlaceProductVariantResponse {
  id: string
  objectID: string
  title: string
  sku: string
  variantGroupCode?: string
  originalPrice: number
  discountPrice?: number
  promotions?: IMarketplaceProductPromotion[]
  returnable?: boolean
  images: IProductImage[]
  optionValues: IGetMarketplaceProductVariantOption[]
  quantity: number
  issues: IIssue[]
  createdAt?: string
  information: IMarketplaceProductInformationEntry[]
}

export interface IGetMarketplaceProductVariantOption {
  id: string
  optionId: string
  displayName: string
  value: string
}

export enum MarketplaceProductOptionType {
  DROPDOWN = 'DROPDOWN',
}

export interface IMarketplaceVisualGuide {
  name: string
  url: string
  description: string
}

export interface IGetMarketplaceProductOption {
  id: string
  type: MarketplaceProductOptionType
  displayName: string
  visualGuide: IMarketplaceVisualGuide | null
  values: { id: string; label: string; colourHexCode?: string }[]
}

export interface IGetMarketplaceProductResponse {
  id: string
  title: string
  description?: string
  careInstructions?: string
  warnings?: string
  warranty?: string
  brand: string
  category: Pick<ICategory, 'path'> | null
  variants: IGetMarketPlaceProductVariantResponse[]
  options: IGetMarketplaceProductOption[]
}

export interface IMarketplaceProductOptionValue {
  id: string
  label: string
  colourHexCode?: string
}

export interface IMarketplaceProductOption {
  id: string
  type: MarketplaceProductOptionType
  displayName: string
  visualGuide: IMarketplaceVisualGuide | null
  values: IMarketplaceProductOptionValue[]
}

export interface IMarketplaceProductVariant {
  id: string
  objectID: string
  title: string
  sku: string
  variantGroupCode?: string
  originalPrice?: number
  discountPrice?: number
  promotions?: IMarketplaceProductPromotion[]
  returnable?: boolean
  images: IProductImage[]
  // TODO: update or remove
  optionValues: IGetMarketplaceProductVariantOption[]
  quantity: number
  issues: IIssue[]
  createdAt?: string
  information: IMarketplaceProductInformationEntry[]
}

interface IMarketplaceProduct {
  id: string
  title: string
  shop: IMarketplaceProductShop
  description?: string
  careInstructions?: string
  warnings?: string
  warranty?: string
  variants: IMarketplaceProductVariant[]
  options: IMarketplaceProductOption[]
}

export interface IMarketplaceProductShop {
  retailerId?: string
  retailerSlug?: string
  id: number
  name?: string
  logoUrl?: string
}

export interface IGetMarketplaceShopResponse {
  id: number
  name: string
  retailer?: IRetailer
  tradingState: TradingState
}

export interface IRetailer {
  sys: {
    id: string
  }
  slug: string
  retailerLogoSet?: IRetailerLogo
  retailerSizeGuide?: string
}

interface IRetailerLogo {
  coloured?: {
    url: string
  }
}

export enum StockLevel {
  IN_STOCK = 'IN_STOCK',
  LOW_STOCK = 'LOW_STOCK',
  UNAVAILABLE = 'UNAVAILABLE',
}

export enum TradingState {
  Open = 'OPEN',
  Closed = 'CLOSED',
  ClosedPermanently = 'CLOSED_PERMANENTLY',
}

export interface IIssueWithMessage extends IIssue {
  message: string
}

export interface IMarketplaceProductAvailability {
  stockLevel: StockLevel
}

export class MarketplaceProduct implements IMarketplaceProduct {
  public readonly id: string
  public readonly title: string
  public readonly shop: IMarketplaceProductShop
  public readonly brand: string
  public readonly category: Pick<ICategory, 'path'> | null
  public readonly description?: string
  public readonly careInstructions?: string
  public readonly warnings?: string
  public readonly warranty?: string
  public readonly variants: IMarketplaceProductVariant[]
  public readonly options: IMarketplaceProductOption[]
  public readonly modifiers: IProductModifier[]
  public readonly url?: string
  public readonly canonicalUrl?: string

  public constructor(
    marketplaceProduct: IGetMarketplaceProductResponse,
    marketplaceShop: IGetMarketplaceShopResponse,
    url?: string,
    searchParams?: URLSearchParams
  ) {
    this.id = marketplaceProduct.id
    this.title = marketplaceProduct.title
    this.shop = {
      retailerId: marketplaceShop.retailer?.sys.id,
      retailerSlug: marketplaceShop.retailer?.slug,
      id: marketplaceShop.id,
      name: marketplaceShop.name,
      logoUrl: marketplaceShop.retailer?.retailerLogoSet?.coloured?.url,
    }
    this.brand = marketplaceProduct.brand
    this.category = marketplaceProduct.category
    this.description = marketplaceProduct.description
    this.careInstructions = marketplaceProduct.careInstructions
    this.warnings = marketplaceProduct.warnings
    this.warranty = marketplaceProduct.warranty
    this.variants = this.toProductVariants(marketplaceProduct.variants)
    // TODO: implement options & modifiers
    // or remove current use of these attributes
    this.options = this.toProductOptions(
      marketplaceProduct.options,
      this.variants
    )
    this.modifiers = []

    this.url = url
      ? this.toProductUrl(
          url,
          marketplaceShop.id,
          marketplaceProduct.variants[0].variantGroupCode || '',
          marketplaceProduct.title,
          marketplaceProduct.brand,
          searchParams
        )
      : undefined

    if (url && marketplaceProduct.variants[0]) {
      this.canonicalUrl = this.toProductUrl(
        url,
        marketplaceShop.id,
        marketplaceProduct.variants[0].variantGroupCode || '',
        marketplaceProduct.title,
        marketplaceProduct.brand
      )
    }
  }

  private toProductUrl(
    url: string,
    shopId: number,
    productId: string,
    title: string,
    brand?: string,
    searchParams?: URLSearchParams
  ): string {
    const slug = brand ? stringToSlug(`${brand}-${title}`) : stringToSlug(title)
    const e = encodeURIComponent
    const u = new URL(
      `/shop/product/${e(shopId)}/${e(productId)}/${e(slug)}`,
      url
    )

    if (searchParams) {
      searchParams.forEach((v, k) => u.searchParams.append(k, v))
    }

    return u.toString()
  }

  private toProductOptions = (
    options: IGetMarketplaceProductOption[] | undefined,
    variants: IMarketplaceProductVariant[] | undefined
  ): IGetMarketplaceProductOption[] => {
    const filteredOptions =
      options?.filter((o): o is IGetMarketplaceProductOption => o !== null) ||
      []
    const result = []
    for (const option of filteredOptions) {
      const values = [...option.values]
      for (const variant of variants || []) {
        const ov = variant.optionValues.find(
          ({ optionId }) => optionId === option.id
        )
        if (!ov || values.some(({ id }) => ov.id === id)) {
          continue
        }
        values.push({
          id: ov.id,
          label: ov.value,
        })
      }
      result.push({
        ...option,
        values,
      })
    }
    return result
  }

  private toProductVariants(
    variants?: IMarketplaceProductVariant[]
  ): IMarketplaceProductVariant[] {
    const makeOtherOption = (
      optionId: string,
      displayName: string
    ): IGetMarketplaceProductVariantOption => ({
      optionId,
      displayName,
      id: 'other',
      value: 'Other',
    })

    const optionIds: string[][] = Array.from(
      (variants || [])
        .reduce((map, variant) => {
          for (const ov of variant.optionValues || []) {
            map.set(ov.optionId, ov.displayName)
          }
          return map
        }, new Map<string, string>())
        .entries()
    )

    return (variants || []).map(variant => {
      const optionValues = optionIds.map(
        ([optionId, displayName]) =>
          variant.optionValues?.find(ov => ov.optionId === optionId) ||
          makeOtherOption(optionId, displayName)
      )
      return { ...variant, optionValues }
    })
  }

  public structuredData(sku: string): Product {
    const selectedVariant = this.variants.filter(v => v.sku === sku)[0]
    return this.productWithOffer(selectedVariant || this.variants[0])
  }

  private productWithOffer(variant: IMarketplaceProductVariant): Product {
    return {
      '@type': 'Product',
      name: this.title,
      description: this.description,
      brand: {
        '@type': 'Brand',
        name: this.brand,
      },
      image: [variant.images[0].url],
      offers: {
        '@type': 'Offer',
        price: variant.discountPrice ?? (variant.originalPrice || 0),
        priceCurrency: 'AUD',
        availability: variant.quantity > 0 ? 'InStock' : 'OutOfStock',
        url: this.url,
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: 'AU',
          },
        },
      },
    }
  }
}
